import { Add } from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useWindowSize } from "usehooks-ts";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import TemplateIcon from "@/components/Icons/TemplateIcon.tsx";
import SimpleModalFix from "@/components/SimpleModalFix.tsx";
import LeftPanel from "@/components/Templates/LeftPanel.tsx";
import RightPanel from "@/components/Templates/RightPanel.tsx";
import { WsTemplateExtended } from "@/components/Templates/templates.ts";
import useTemplates from "@/components/Templates/useTemplates.ts";
import { ampli } from "@/config";
import Locator from "@/locator.ts";
import { FeedContext } from "@/models/FeedContextProvider.tsx";

export default function TemplateContainer({
  allowTemplateUse,
  showRightPanel,
  header,
  setDisableCloseModal,
  useSelectedTemplate,
}: {
  allowTemplateUse?: boolean;
  showRightPanel: boolean;
  header?: React.ReactNode;
  setDisableCloseModal?: (value: boolean) => void;
  useSelectedTemplate?: (tts: string) => void;
}) {
  const { setTemplatesModal } = useContext(FeedContext);
  const theme = useTheme();
  const templateContainerRef = useRef<HTMLDivElement>(null);
  const [leftColHeight, setLeftColHeight] = useState<number | null>(null);
  const [newTemplate, setNewTemplate] = useState<boolean>(false);
  const [template, setTemplate] = useState<WsTemplateExtended | null>(null);
  const [disabledIds, setDisabledIds] = useState<string[]>([]);
  const { templates, count } = useTemplates();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const { height } = useWindowSize();
  const newTemplateButtonHeight = showRightPanel ? 54 : 0;
  const leftPanelWidth = showRightPanel ? "300px" : "100%";

  useEffect(() => {
    if (templateContainerRef && height) {
      setLeftColHeight(() => templateContainerRef.current?.offsetHeight - newTemplateButtonHeight - 80 || null);
    }
  }, [templateContainerRef, height, newTemplateButtonHeight]);

  const createNewTemplate = () => {
    setTemplate(null);
    setNewTemplate(true);
    ampli.newTemplate();
  };

  const setTemplateValue = (value: WsTemplateExtended) => {
    setTemplate(() => value);
    setNewTemplate(() => false);
  };

  const closeModal = () => {
    setTemplatesModal(false);
    setTemplate(() => null);
    setNewTemplate(() => false);
  };

  return (
    <>
      {header && (
        <Box sx={{ height: "70px", p: 2, display: "flex" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography component="h3" sx={{ fontSize: "1.75rem", fontWeight: 700 }}>
              All templates ({count || 0})
            </Typography>
          </Box>
          <Box>
            <Button
              data-sb={TestIds.templates.create}
              sx={{ gap: 2 }}
              aria-label={Locator.workspaceNav.templates.createTemplate}
              variant="contained"
              onClick={() => createNewTemplate()}
            >
              <TemplateIcon role="img" /> New Template
            </Button>
          </Box>
        </Box>
      )}
      <Box ref={templateContainerRef} sx={{ display: "flex", gap: 1, height: "100%" }}>
        <Box sx={{ width: leftPanelWidth }}>
          {showRightPanel && (
            <Box sx={{ height: "44px", mb: "10px" }}>
              <Button
                disabled={newTemplate}
                onClick={() => createNewTemplate()}
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  background: (theme) => theme.palette.secondary.main,
                }}
              >
                <Box sx={{ flex: 1, textAlign: "left" }}>New Template</Box>
                <Box sx={{ display: "flex" }}>
                  <Add role="img" />
                </Box>
              </Button>
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              height: `${leftColHeight}px`,
              overflowY: "auto",
              width: leftPanelWidth,
            }}
          >
            <LeftPanel
              activeId={template?.id ?? null}
              setActiveTemplate={(value) => setTemplateValue(value)}
              templates={templates}
              additionalButtons={showRightPanel}
            />
          </Box>
        </Box>
        {showRightPanel ? (
          <Box
            sx={{
              flexGrow: 1,
              borderLeft: `solid 1px ${theme.palette.secondary.light}`,
              pl: 1,
            }}
          >
            <RightPanel
              useSelectedTemplate={useSelectedTemplate}
              setDisableCloseModal={setDisableCloseModal ?? (() => {})}
              setActiveTemplate={(value) => setTemplateValue(value)}
              key={template?.id || "new"}
              newTemplate={newTemplate}
              allowTemplateUse={allowTemplateUse}
              template={template}
              closeModal={() => closeModal()}
            />
          </Box>
        ) : (
          <SimpleModalFix
            confirmDiscard={hasChanges}
            title={newTemplate ? "New Template" : template?.canEdit ? "Edit Template" : "View Template"}
            closeModal={() => closeModal()}
            open={!!template?.id || newTemplate}
            key={template?.id || "new"}
            sx={{
              boxShadow: hasChanges ? `0 0 0 2px ${theme.palette.info.main}` : "",
            }}
          >
            <RightPanel
              closeModal={() => closeModal()}
              setDisableCloseModal={setHasChanges}
              setActiveTemplate={(value) => setTemplateValue(value)}
              newTemplate={newTemplate}
              allowTemplateUse={allowTemplateUse}
              template={template}
            />
          </SimpleModalFix>
        )}
      </Box>
    </>
  );
}
